/**
 * Telemetry fetch guard — swallows network errors from blocked telemetry
 * requests so they never reach the console as unhandled rejections.
 *
 * Why a fetch wrapper instead of an unhandledrejection filter?
 *
 * The previous approach (see earlier revisions of main.tsx) checked
 * `error.stack` for strings like "posthog.com" or "betterstack.com" and
 * suppressed matching rejections. That works in dev source-map builds, but
 * fails in production for two reasons:
 *
 *   1. posthog-js and @logtail/browser are bundled into the main app chunk,
 *      so their frames appear as `index-abc123.js` — no telemetry host in
 *      the stack at all.
 *   2. Ad blockers (uBlock, AdBlock, Privacy Badger, and Arc/Brave's built-in
 *      shields) wrap `window.fetch` with their own script (e.g. `frame_ant.js`)
 *      and throw a synthetic TypeError from inside that wrapper. The resulting
 *      stack is dominated by the wrapper's frames; the original caller may be
 *      ignore-listed and omitted from the serialized `error.stack` entirely.
 *
 * Wrapping at the URL layer sidesteps both problems. We know the URL at call
 * time (the argument to `fetch`), so we can decide "this is telemetry, swallow
 * its errors" without needing the stack trace to survive minification or
 * extension wrapping.
 *
 * Behaviour:
 *   - For telemetry URLs that FAIL (network error, blocked by extension, DNS
 *     failure, CORS), we catch the error and return a synthetic 204 Response.
 *     PostHog and Logtail treat this as a successful fire-and-forget; they
 *     don't retry, don't throw, and don't log anything visible.
 *   - For telemetry URLs that SUCCEED, we pass through untouched.
 *   - For everything else (our own API, third-party fonts, etc.), we pass
 *     through untouched — errors still propagate to callers as before.
 *
 * Ordering is important: this file must be imported BEFORE any code that
 * captures a reference to `window.fetch` at module load. In practice that
 * means it's the first import in main.tsx, before `posthog-js` or
 * `@logtail/browser`.
 */

// Hosts whose fetch failures are safe to swallow. These are all fire-and-forget
// telemetry endpoints — missing events degrade observability but do not change
// app behaviour. If you add an endpoint here that ISN'T fire-and-forget, you'll
// mask real failures.
const TELEMETRY_URL_PATTERNS: RegExp[] = [
  /(^|\/\/|\.)posthog\.com(\/|$|:)/i, // *.posthog.com, including us.i.posthog.com and us-assets.i.posthog.com
  /(^|\/\/|\.)betterstack\.com(\/|$|:)/i, // *.betterstack.com, including in.logs.betterstack.com
  /(^|\/\/|\.)logtail\.com(\/|$|:)/i, // legacy Logtail endpoint, still referenced by some SDK versions
];

function isTelemetryUrl(url: string): boolean {
  return TELEMETRY_URL_PATTERNS.some((rx) => rx.test(url));
}

/**
 * Extract a URL string from the polymorphic first argument to `fetch`, which
 * can be a string, a URL object, or a Request instance. Anything unexpected
 * falls back to the empty string → treated as non-telemetry → passthrough.
 */
function extractUrl(input: RequestInfo | URL): string {
  if (typeof input === 'string') return input;
  if (input instanceof URL) return input.href;
  // Request — has a `url` getter. Guarded so SSR/test environments without
  // a global Request don't trip.
  if (typeof Request !== 'undefined' && input instanceof Request) return input.url;
  return '';
}

/**
 * Install the guard. Safe to call multiple times — subsequent calls are no-ops.
 * Returns true if the guard was (or had already been) installed, false if the
 * environment doesn't have a fetch to wrap (SSR, tests using jsdom without
 * fetch polyfill).
 */
export function installTelemetryFetchGuard(): boolean {
  if (typeof window === 'undefined' || typeof window.fetch !== 'function') {
    return false;
  }

  // Idempotency marker. Multiple imports (HMR, test setup) must not stack
  // wrappers, or the call chain gets arbitrarily deep and error messages
  // become harder to read.
  const w = window as Window & { __vellumTelemetryGuardInstalled?: boolean };
  if (w.__vellumTelemetryGuardInstalled) return true;

  const originalFetch = window.fetch.bind(window);

  const guardedFetch: typeof window.fetch = async (input, init) => {
    const url = extractUrl(input);
    const isTelemetry = url ? isTelemetryUrl(url) : false;

    try {
      return await originalFetch(input, init);
    } catch (err) {
      if (isTelemetry) {
        // Synthetic response — 204 No Content so consumers see a successful
        // request with no body. `ok` is true for 2xx, so SDK retry logic
        // that checks `response.ok` won't loop.
        return new Response(null, {
          status: 204,
          statusText: 'No Content (telemetry blocked by client)',
        });
      }
      throw err;
    }
  };

  window.fetch = guardedFetch;
  w.__vellumTelemetryGuardInstalled = true;
  return true;
}

// Exported for unit tests — don't use directly in app code.
export const __internal = { isTelemetryUrl, TELEMETRY_URL_PATTERNS };
